<template>
  <div class="canvas-wrap" ref="wrap">
    <!-- SVG graph -->
    <svg ref="svg" class="graph-svg">
      <defs>
        <!-- Glow filters -->
        <filter id="glow-gold" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="glow-blue" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="glow-red" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <!-- Arrowhead markers -->
        <marker id="arr-blue" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
          <path d="M0,0.5 L0,5.5 L7,3 z" fill="#4477ff" opacity="0.85"/>
        </marker>
        <marker id="arr-red" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
          <path d="M0,0.5 L0,5.5 L7,3 z" fill="#ff4444" opacity="0.85"/>
        </marker>
        <!-- Node radial gradients -->
        <radialGradient id="rg-origin" cx="35%" cy="30%" r="65%">
          <stop offset="0%" stop-color="#ffe066"/>
          <stop offset="100%" stop-color="#cc5500"/>
        </radialGradient>
        <radialGradient id="rg-before" cx="35%" cy="30%" r="65%">
          <stop offset="0%" stop-color="#88bbff"/>
          <stop offset="100%" stop-color="#0a2288"/>
        </radialGradient>
        <radialGradient id="rg-after" cx="35%" cy="30%" r="65%">
          <stop offset="0%" stop-color="#ff9999"/>
          <stop offset="100%" stop-color="#880011"/>
        </radialGradient>
      </defs>
    </svg>

    <!-- Floating node detail card -->
    <transition name="card-pop">
      <div v-if="selected" class="node-card" :style="cardStyle" @click.stop>
        <div class="ncard-head" :class="'dir-' + selected.direction">
          <span class="dir-badge">{{ dirLabel[selected.direction] }}</span>
          <span class="addr-mono" :title="selected.id">{{ selected.id }}</span>
          <button class="btn-x" @click="selected = null">✕</button>
        </div>
        <div class="ncard-body">
          <div class="stat"><span>Balance</span><strong>{{ fmtTrx(selected.balance) }} TRX</strong></div>
          <div class="stat"><span>TRX received</span><strong class="green">+{{ fmtTrx(selected.totalIn) }}</strong></div>
          <div class="stat"><span>TRX sent</span><strong class="red">−{{ fmtTrx(selected.totalOut) }}</strong></div>
          <div class="stat"><span>Net flow</span><strong :class="selected.totalIn - selected.totalOut >= 0 ? 'green' : 'red'">{{ fmtTrx(selected.totalIn - selected.totalOut) }}</strong></div>
          <div class="stat"><span>TXs visible</span><strong>{{ selected.txCount }}</strong></div>
          <div class="stat"><span>Graph level</span><strong>{{ selected.level > 0 ? '+' : '' }}{{ selected.level }}</strong></div>
        </div>
        <div class="ncard-actions">
          <button class="act-btn primary" @click="$emit('navigate', selected.id)">⬤ Make origin</button>
          <a class="act-btn" :href="'https://tronscan.org/#/address/' + selected.id" target="_blank">Tronscan ↗</a>
        </div>
      </div>
    </transition>

    <!-- Edge tooltip -->
    <div v-if="edgeTip" class="edge-tip" :style="{ left: edgeTip.x + 'px', top: edgeTip.y + 'px' }">
      {{ fmtTrx(edgeTip.amount) }} TRX
    </div>

    <!-- Legend -->
    <div class="legend">
      <div class="leg-row"><span class="dot" style="background:radial-gradient(#ffe066,#cc5500)"></span>Origin</div>
      <div class="leg-row"><span class="dot" style="background:radial-gradient(#88bbff,#0a2288)"></span>Before (funded)</div>
      <div class="leg-row"><span class="dot" style="background:radial-gradient(#ff9999,#880011)"></span>After (received)</div>
      <div class="leg-row edge-row">
        <svg width="32" height="8"><line x1="1" y1="4" x2="31" y2="4" stroke="#4477ff" stroke-width="1.5" stroke-dasharray="6 4"/></svg>
        Inflow
      </div>
      <div class="leg-row edge-row">
        <svg width="32" height="8"><line x1="1" y1="4" x2="31" y2="4" stroke="#ff4444" stroke-width="1.5" stroke-dasharray="6 4"/></svg>
        Outflow
      </div>
    </div>

    <!-- Controls hint -->
    <div class="hint">Scroll to zoom · Drag canvas to pan · Click node to inspect · Drag node to pin</div>

    <!-- Reset zoom button -->
    <button class="btn-reset" @click="resetZoom" title="Reset view">⌖</button>
  </div>
</template>

<script>
import * as d3 from 'd3';

const DIR_GRAD = { origin: 'url(#rg-origin)', before: 'url(#rg-before)', after: 'url(#rg-after)' };
const DIR_HALO = { origin: '#ffaa00', before: '#2255dd', after: '#cc1122' };
const DIR_FILTER = { origin: 'url(#glow-gold)', before: 'url(#glow-blue)', after: 'url(#glow-red)' };
const DIR_ARROW  = { origin: 'url(#arr-red)', before: 'url(#arr-blue)', after: 'url(#arr-red)' };
const DIR_STROKE = { before: '#4477ff', after: '#ff4444', origin: '#ff4444' };

function nodeR(d) {
  if (d.direction === 'origin') return 32;
  const vol = d.totalIn + d.totalOut;
  return Math.max(11, Math.min(30, 9 + Math.cbrt(vol) * 1.4));
}

function arcPath(d) {
  const s = d.source, t = d.target;
  const dx = t.x - s.x, dy = t.y - s.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const dr = dist * 0.55;
  const sr = nodeR(s) + 2;
  const tr = nodeR(t) + 9; // leave room for arrowhead
  return [
    `M${s.x + (dx / dist) * sr},${s.y + (dy / dist) * sr}`,
    `A${dr},${dr} 0 0,1`,
    `${t.x - (dx / dist) * tr},${t.y - (dy / dist) * tr}`,
  ].join(' ');
}

export default {
  name: 'TxGraphCanvas',
  props: {
    graph: { type: Object, required: true },
    origin: { type: String, default: '' },
  },
  emits: ['navigate'],

  data() {
    return {
      selected: null,
      edgeTip: null,
      transform: d3.zoomIdentity,
      simulation: null,
      zoomBehavior: null,
      dirLabel: { origin: '⬤ ORIGIN', before: '← BEFORE', after: '→ AFTER' },
    };
  },

  computed: {
    cardStyle() {
      if (!this.selected) return {};
      const [sx, sy] = this.transform.apply([this.selected.x || 0, this.selected.y || 0]);
      const r = nodeR(this.selected);
      const wrapW = this.$refs.wrap?.clientWidth || 800;
      // Flip to left side if card would overflow right edge
      const cardW = 250;
      const left = sx + r + 18 + cardW > wrapW ? sx - r - cardW - 18 : sx + r + 18;
      return { left: left + 'px', top: Math.max(10, sy - 90) + 'px' };
    },
  },

  watch: {
    graph(g) {
      this.selected = null;
      this.$nextTick(() => this.render(g));
    },
  },

  mounted() {
    if (this.graph) this.$nextTick(() => this.render(this.graph));
    window.addEventListener('resize', this.onResize);
  },

  beforeDestroy() {
    if (this.simulation) this.simulation.stop();
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    fmtTrx(v) {
      if (v == null) return '—';
      return Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
    },

    onResize() {
      if (this.graph) this.render(this.graph);
    },

    resetZoom() {
      const W = this.$refs.wrap.clientWidth;
      const H = this.$refs.wrap.clientHeight;
      d3.select(this.$refs.svg)
        .transition().duration(650).ease(d3.easeCubicInOut)
        .call(this.zoomBehavior.transform, d3.zoomIdentity.translate(W / 2, H / 2));
    },

    zoomTo(d) {
      const W = this.$refs.wrap.clientWidth;
      const H = this.$refs.wrap.clientHeight;
      const k = 3.5;
      d3.select(this.$refs.svg)
        .transition().duration(800).ease(d3.easeCubicInOut)
        .call(this.zoomBehavior.transform,
          d3.zoomIdentity.translate(W / 2 - d.x * k, H / 2 - d.y * k).scale(k));
    },

    render(graph) {
      if (this.simulation) this.simulation.stop();
      const wrap = this.$refs.wrap;
      const svgEl = this.$refs.svg;
      if (!wrap || !svgEl) return;

      const W = wrap.clientWidth;
      const H = wrap.clientHeight;
      const self = this;

      // Deep clone so D3 can add x/y without mutating prop
      const nodes = graph.nodes.map(n => ({ ...n }));
      const edges = graph.edges.map(e => ({ ...e }));

      // ── SVG ──────────────────────────────────────────────────
      const svg = d3.select(svgEl).attr('width', W).attr('height', H);
      svg.selectAll('g.scene').remove();
      const g = svg.append('g').attr('class', 'scene');

      // ── Zoom ─────────────────────────────────────────────────
      this.zoomBehavior = d3.zoom()
        .scaleExtent([0.04, 14])
        .on('zoom', (event) => {
          g.attr('transform', event.transform);
          self.transform = event.transform;
          const k = event.transform.k;

          // Semantic zoom: progressively reveal more text as user zooms in
          nodeG.select('.lbl-full')
            .attr('opacity', k > 1.8 ? Math.min(1, (k - 1.8) * 1.5) : 0);
          nodeG.select('.lbl-bal')
            .attr('opacity', k > 3   ? Math.min(1, (k - 3) * 2)   : 0);
          nodeG.select('.lbl-txc')
            .attr('opacity', k > 4   ? Math.min(1, (k - 4) * 2)   : 0);
          // Scale short label inversely so it stays readable while zooming
          nodeG.select('.lbl-short')
            .attr('font-size', d => Math.max(4, (d.direction === 'origin' ? 9 : 7.5) / Math.max(1, Math.sqrt(k))) + 'px');
        });

      svg.call(this.zoomBehavior);
      svg.on('click.deselect', () => { self.selected = null; });

      // ── Simulation ───────────────────────────────────────────
      this.simulation = d3.forceSimulation(nodes)
        .force('link',
          d3.forceLink(edges)
            .id(d => d.id)
            .distance(d => {
              const sl = (d.source.level || 0), tl = (d.target.level || 0);
              return 160 + Math.abs(sl - tl) * 30;
            })
            .strength(0.55)
        )
        .force('charge', d3.forceManyBody().strength(-800))
        .force('x', d3.forceX(d => (d.level || 0) * 240).strength(0.38))
        .force('y', d3.forceY(0).strength(0.06))
        .force('collision', d3.forceCollide(d => nodeR(d) + 22))
        .alphaDecay(0.022);

      // ── Edge base ────────────────────────────────────────────
      const edgeLayer = g.append('g').attr('class', 'edges');

      const edgeBase = edgeLayer.selectAll('.eb')
        .data(edges).join('path')
        .attr('class', 'eb')
        .attr('fill', 'none')
        .attr('stroke', d => {
          const srcNode = nodes.find(n => n.id === d.source || n.id === d.source?.id);
          return DIR_STROKE[srcNode?.direction] || '#888';
        })
        .attr('stroke-opacity', 0.35)
        .attr('stroke-width', d => Math.max(1, Math.min(6, Math.log(d.amount + 1) * 0.9)))
        .attr('marker-end', d => {
          const srcNode = nodes.find(n => n.id === d.source || n.id === d.source?.id);
          return DIR_ARROW[srcNode?.direction] || 'url(#arr-red)';
        })
        .on('mouseenter', (event, d) => {
          const [mx, my] = d3.pointer(event, wrap);
          self.edgeTip = { x: mx + 10, y: my - 20, amount: d.amount };
        })
        .on('mouseleave', () => { self.edgeTip = null; });

      // ── Animated flow paths ───────────────────────────────────
      const edgeFlow = edgeLayer.selectAll('.ef')
        .data(edges).join('path')
        .attr('class', d => {
          const srcNode = nodes.find(n => n.id === d.source || n.id === d.source?.id);
          return 'ef ' + (srcNode?.direction === 'before' ? 'ef-in' : 'ef-out');
        })
        .attr('fill', 'none')
        .attr('stroke-width', 2)
        .attr('pointer-events', 'none');

      // ── Node groups ───────────────────────────────────────────
      const nodeLayer = g.append('g').attr('class', 'nodes');

      const nodeG = nodeLayer.selectAll('.ng')
        .data(nodes, d => d.id)
        .join(enter => {
          const ng = enter.append('g')
            .attr('class', 'ng')
            .attr('data-dir', d => d.direction)
            .attr('cursor', 'pointer')
            .call(
              d3.drag()
                .on('start', (ev, d) => {
                  if (!ev.active) self.simulation.alphaTarget(0.3).restart();
                  d.fx = d.x; d.fy = d.y;
                })
                .on('drag', (ev, d) => { d.fx = ev.x; d.fy = ev.y; })
                .on('end', (ev, d) => {
                  if (!ev.active) self.simulation.alphaTarget(0);
                  d.fx = null; d.fy = null;
                })
            )
            .on('click', (event, d) => {
              event.stopPropagation();
              self.selected = d;
              self.zoomTo(d);
            });

          // Outer pulse halo
          ng.append('circle')
            .attr('class', 'halo')
            .attr('r', d => nodeR(d) + 3)
            .attr('fill', d => DIR_HALO[d.direction] || '#888')
            .attr('opacity', 0.12)
            .attr('filter', d => DIR_FILTER[d.direction] || 'none');

          // Main circle — animate scale-in with stagger
          ng.append('circle')
            .attr('class', 'body')
            .attr('r', 0)
            .attr('fill', d => DIR_GRAD[d.direction] || '#555')
            .attr('stroke', '#fff')
            .attr('stroke-opacity', 0.55)
            .attr('stroke-width', d => d.direction === 'origin' ? 2.5 : 1.5)
            .transition()
            .delay((_, i) => i * 35)
            .duration(750)
            .ease(d3.easeBounceOut)
            .attr('r', d => nodeR(d));

          // Hover: brighten halo
          ng.on('mouseenter', function(ev, d) {
            d3.select(this).select('.halo')
              .transition().duration(180)
              .attr('r', nodeR(d) + 14)
              .attr('opacity', 0.45);
          }).on('mouseleave', function(ev, d) {
            d3.select(this).select('.halo')
              .transition().duration(280)
              .attr('r', nodeR(d) + 3)
              .attr('opacity', d === self.selected ? 0.4 : 0.12);
          });

          // Short label (inside / below circle, always visible)
          ng.append('text')
            .attr('class', 'lbl-short')
            .attr('text-anchor', 'middle')
            .attr('dy', d => nodeR(d) >= 18 ? '0.35em' : nodeR(d) + 12 + 'px')
            .attr('font-size', d => (d.direction === 'origin' ? 9 : 7.5) + 'px')
            .attr('fill', d => nodeR(d) >= 18 ? '#fff' : '#ccc')
            .attr('pointer-events', 'none')
            .text(d => d.label);

          // Full address — appears at zoom > 1.8
          ng.append('text')
            .attr('class', 'lbl-full')
            .attr('text-anchor', 'middle')
            .attr('dy', d => nodeR(d) + 14)
            .attr('font-size', '7px')
            .attr('fill', '#99aacc')
            .attr('opacity', 0)
            .attr('pointer-events', 'none')
            .text(d => d.id);

          // Balance line — appears at zoom > 3
          ng.append('text')
            .attr('class', 'lbl-bal')
            .attr('text-anchor', 'middle')
            .attr('dy', d => nodeR(d) + 24)
            .attr('font-size', '6.5px')
            .attr('fill', '#ffcc44')
            .attr('opacity', 0)
            .attr('pointer-events', 'none')
            .text(d => d.balance ? Number(d.balance).toFixed(0) + ' TRX' : '');

          // TX count line — appears at zoom > 4
          ng.append('text')
            .attr('class', 'lbl-txc')
            .attr('text-anchor', 'middle')
            .attr('dy', d => nodeR(d) + 33)
            .attr('font-size', '6px')
            .attr('fill', '#888')
            .attr('opacity', 0)
            .attr('pointer-events', 'none')
            .text(d => d.txCount ? d.txCount + ' txs' : '');

          return ng;
        });

      // Origin node: continuous pulse animation on halo
      nodeG.filter(d => d.direction === 'origin')
        .select('.halo')
        .attr('opacity', 0.25);

      // ── Simulation tick ───────────────────────────────────────
      this.simulation.on('tick', () => {
        edgeBase.attr('d', arcPath);
        edgeFlow.attr('d', arcPath);
        nodeG.attr('transform', d => `translate(${d.x ?? 0},${d.y ?? 0})`);
      });

      // ── Initial view: center on origin node ──────────────────
      svg.call(this.zoomBehavior.transform, d3.zoomIdentity.translate(W / 2, H / 2));

      // After simulation settles (1s), gently recenter on origin
      setTimeout(() => {
        const origin = nodes.find(n => n.direction === 'origin');
        if (origin && origin.x != null) {
          svg.transition().duration(900).ease(d3.easeCubicInOut)
            .call(this.zoomBehavior.transform,
              d3.zoomIdentity.translate(W / 2 - origin.x, H / 2 - origin.y));
        }
      }, 1200);
    },
  },
};
</script>

<style scoped>
.canvas-wrap {
  width: 100%;
  height: 100%;
  position: relative;
  background: radial-gradient(ellipse at 50% 40%, #0e0e22 0%, #04040a 100%);
  overflow: hidden;
}
.graph-svg { display: block; }

/* ── Edge flow animation ── */
:deep(.ef) {
  stroke-dasharray: 8 5;
  animation: flowAnim 1.5s linear infinite;
}
:deep(.ef-in)  { stroke: rgba(68,119,255,0.7); }
:deep(.ef-out) { stroke: rgba(255,68,68,0.7); }
@keyframes flowAnim { to { stroke-dashoffset: -26; } }

/* ── Origin node pulse ── */
:deep(.ng[data-dir="origin"] .halo) {
  animation: originPulse 2.2s ease-in-out infinite;
}
@keyframes originPulse {
  0%,100% { opacity: 0.2; r: 36px; }
  50%      { opacity: 0.5; r: 44px; }
}

/* ── Node detail card ── */
.node-card {
  position: absolute;
  width: 250px;
  background: rgba(10, 10, 22, 0.97);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  box-shadow: 0 6px 32px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04);
  overflow: hidden;
  z-index: 20;
  pointer-events: auto;
}
.ncard-head {
  display: flex;
  align-items: center;
  gap: .45rem;
  padding: .5rem .75rem;
  background: #0d0d1e;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  font-size: .68rem;
}
.ncard-head.dir-origin { border-left: 3px solid #ff8800; }
.ncard-head.dir-before { border-left: 3px solid #3366ee; }
.ncard-head.dir-after  { border-left: 3px solid #cc1122; }
.dir-badge {
  font-size: .62rem;
  font-weight: 700;
  letter-spacing: .04em;
  white-space: nowrap;
  color: #aaa;
}
.addr-mono {
  flex: 1;
  font-family: 'Courier New', monospace;
  font-size: .62rem;
  color: #ccc;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.btn-x {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: .8rem;
  padding: 0 0 0 .3rem;
  line-height: 1;
  flex-shrink: 0;
}
.btn-x:hover { color: #fff; }

.ncard-body { padding: .45rem .75rem; font-size: .76rem; }
.stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .18rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.stat:last-child { border-bottom: none; }
.stat span { color: #777; }
.stat strong { color: #ddd; font-weight: 500; }
.stat strong.green { color: #55ee88; }
.stat strong.red   { color: #ff7070; }

.ncard-actions {
  display: flex;
  gap: .4rem;
  padding: .4rem .75rem;
  border-top: 1px solid rgba(255,255,255,0.07);
  background: #0d0d1e;
}
.act-btn {
  flex: 1;
  text-align: center;
  font-size: .7rem;
  padding: .28rem .4rem;
  background: #181830;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 5px;
  color: #bbb;
  cursor: pointer;
  text-decoration: none;
  transition: background .15s, color .15s;
}
.act-btn:hover, .act-btn.primary:hover { background: #e00010; border-color: #e00010; color: #fff; }
.act-btn.primary { border-color: rgba(255,80,0,0.4); color: #ffaa55; }

/* Card transition */
.card-pop-enter-active, .card-pop-leave-active {
  transition: opacity .2s, transform .2s;
}
.card-pop-enter, .card-pop-leave-to {
  opacity: 0;
  transform: scale(0.92) translateY(6px);
}

/* Edge tooltip */
.edge-tip {
  position: absolute;
  background: rgba(10,10,22,0.92);
  border: 1px solid #333;
  border-radius: 5px;
  padding: 3px 8px;
  font-size: .7rem;
  color: #ffcc66;
  pointer-events: none;
  z-index: 15;
  white-space: nowrap;
}

/* Legend */
.legend {
  position: absolute;
  bottom: 44px;
  left: 14px;
  background: rgba(8,8,18,0.88);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 9px;
  padding: 8px 13px;
  font-size: .7rem;
  color: #888;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.leg-row { display: flex; align-items: center; gap: 7px; }
.edge-row svg { flex-shrink: 0; }
.dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Controls hint */
.hint {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  font-size: .65rem;
  color: #3a3a55;
  pointer-events: none;
  white-space: nowrap;
}

/* Reset zoom */
.btn-reset {
  position: absolute;
  bottom: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  background: rgba(10,10,22,0.9);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 7px;
  color: #888;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color .15s, border-color .15s;
}
.btn-reset:hover { color: #fff; border-color: #e00010; }
</style>
